import { useState } from "react";
import type { EntityType, EventOption, InteractionEvent } from "../../types";
import { activityFlowState } from "../../utils/activityFlow";
import { START_STEP } from "../../utils/stepOrder";
import { useStepOrderEditing } from "../../hooks/useStepOrderEditing";
import { StepOrderSheet } from "./StepOrderSheet";

interface StepOrderButtonProps {
  entityId: string | null | undefined;
  entityName: string | null | undefined;
  type: EntityType | undefined;
  /** The steps as they run for this activity. */
  options: EventOption[];
  /** What's logged so far, so the sheet opens at the step you're on. */
  events: InteractionEvent[];
  className?: string;
  /** Called after a save, for pages that read the place once. */
  onSaved?: () => void;
}

/**
 * "Edit buttons": opens the button-order sheet at the step the activity is
 * on, so the order can be fixed the moment it's wrong. Hidden from people who
 * can't change places.
 */
export function StepOrderButton({ entityId, entityName, type, options, events, className, onSaved }: StepOrderButtonProps) {
  const [open, setOpen] = useState(false);
  const editing = useStepOrderEditing(entityId, entityName, type);
  if (!editing || options.length === 0) return null;

  const current = activityFlowState(options, events).current;
  return (
    <>
      <button
        type="button"
        className={className ?? "btn-link"}
        onClick={() => setOpen(true)}
        title="Change which steps get a button, and their order"
      >
        Edit buttons
      </button>
      <StepOrderSheet
        open={open}
        onClose={() => setOpen(false)}
        options={options}
        initialStepId={current?.id ?? START_STEP}
        title={entityName ? `${entityName}: steps` : "Steps"}
        targets={editing.targets}
        onSave={async (draft, edited, target) => {
          await editing.save(draft, edited, target);
          onSaved?.();
        }}
        loadHistory={editing.loadHistory}
        note={editing.note}
        entityType={type}
      />
    </>
  );
}
